import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { getDocumentsByCommunication } from '../../utils/services/documentAPI';
import { getLanguageGroupedFlows, getLanguageGroupedBlocks } from '../../utils/services/elementAPI';
import Loader from '../../components/Loader';
import { DocumentCard } from './DocumentCard';
import styled from 'styled-components';
import { Icon, message } from 'antd';
import { SlidersHorizontalIcon, ChevronDownIconSVG } from '../../images/svgIcons';
import { NEW_TEMPLATE_SECTION } from './translationValues';
import ScrollContainerV2 from '../../components/ScrollContainerV2';
import {
  Header,
  HeaderTop,
  HeaderTitle,
  HeaderSubtitle,
  Actions,
  DropdownWrapper,
  SortButton,
  DropdownMenu,
  MenuItem,
  EmptyState,
  EmptyIconWrapper,
  EmptyTitle,
  EmptySubtitle,
  GridLayout,
} from './styles';

const ScrollContainerAdapter = ({ className, ...props }) => (
  <ScrollContainerV2 customClassName={className} {...props} />
);

const GridContainer = styled(ScrollContainerAdapter)`
  width: 100%;
  padding: 32px 80px;
  flex: 1;
  /* overflow-y: auto; handled by ScrollContainerV2 */
  background: linear-gradient(to bottom right, #f9fafb, #f3f4f6) !important;
  transition: padding 0.3s ease;
  
  /* Override ScrollContainerV2 scrollbar styles to match original UI */
  &&::-webkit-scrollbar {
    width: 8px !important;
  }
  &&::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &&::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: #d1d5db !important;
    box-shadow: none !important;
  }
  &&::-webkit-scrollbar-thumb:hover {
     background: #d1d5db !important;
  }
`;

const DocumentGrid = ({
  selectedView,
  organizationView,
  searchQuery,
  qaStatus,
  selectedTags,
  selectedFolder,
  sortBy,
  setSortBy,
  clearAllFilters,
  allLanguages,
}) => {
  const { t } = useTranslation(['NEW_TEMPLATE_SECTION']);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [categoryList, setCategoryList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dataCount, setDataCount] = useState(0);

  const [page, setPage] = useState(1);
  const [refreshPage, setRefreshPage] = useState(0);

  const fetchingRef = useRef(false);
  const hasFetchedAllRef = useRef(false);

  const fetchCategoriesList = async () => {
    try {
      const response = await getAllCategories(
        false,
        selectedView === 'documents' ? 'template' : 'element',
      );
      if (response?.statusCode === 507 && response?.data) {
        const categories = response.data.map(item => ({
          categoryId: item.id,
          categoryName: item.name,
        }));
        setCategoryList(categories);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const initalMountRef = useRef(true);
  useEffect(() => {
    if (!initalMountRef.current) {
      clearAllFilters();
      clearData();
    }
    fetchCategoriesList();
    initalMountRef.current = false;
  }, [selectedView]);

  const fetchTemplates = async (pageNum, isInitialFetch) => {
    if (isInitialFetch) {
      setLoading(true);
    }

    const orgViewKeyMap = {
      categories: 'categoryId',
      folders: 'folderId',
      teams: 'resourceGroupId',
      languages: 'language',
    };

    let statusMap = { waiting: true, rejected: false };

    let payload = {
      page: pageNum,
      limit: 12,
      rgPermission: 'write',

      ...(searchQuery && { name: searchQuery }),
      ...(selectedTags.length && { tags: selectedTags }),
      ...(sortBy === 'name' && { asc: true }),

      ...(selectedFolder !== 'all' &&
        orgViewKeyMap[organizationView] && {
        [orgViewKeyMap[organizationView]]: selectedFolder.split('+')[0],
      }),
    };

    const isAllFetched = response => {
      return isInitialFetch
        ? response.data?.length === response?.dataCount
        : [...templates, ...response.data].length === response?.dataCount;
    };

    if (selectedView === 'documents') {
      payload = {
        ...payload,
        ...(statusMap[qaStatus] !== undefined && {
          waitingForApproval: statusMap[qaStatus],
        }),
      };
      const response = await getDocumentsByCommunication(qaStatus !== null, payload);
      if ([3215, 3216].includes(response?.statusCode) && response?.data) {
        setTemplates(prev => (isInitialFetch ? response.data : [...prev, ...response.data]));
        setDataCount(response?.dataCount);
        if (isAllFetched(response)) {
          hasFetchedAllRef.current = true;
        }
        fetchingRef.current = false;
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FETCH_DOCUMENTS_ERROR.key,
            NEW_TEMPLATE_SECTION.FETCH_DOCUMENTS_ERROR.value,
          ),
        );
      }
    } else if (selectedView === 'reusable_pages') {
      payload = {
        ...payload,
        ...(statusMap[qaStatus] !== undefined && {
          waitingForApproval: statusMap[qaStatus],
        }),
      };

      const response = await getLanguageGroupedFlows(
        pageNum,
        true,
        null,
        searchQuery,
        selectedTags.length ? selectedTags : '',
        'write',
        qaStatus !== null,
        true,
        payload,
      );
      if ([2608, 2607].includes(response?.statusCode) && response?.data) {
        setTemplates(prev => (isInitialFetch ? response.data : [...prev, ...response.data]));
        setDataCount(response?.dataCount);
        if (isAllFetched(response)) {
          hasFetchedAllRef.current = true;
        }
        fetchingRef.current = false;
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FETCH_PAGES_ERROR.key,
            NEW_TEMPLATE_SECTION.FETCH_PAGES_ERROR.value,
          ),
        );
      }
    } else {
      payload = {
        ...payload,
        ...(statusMap[qaStatus] !== undefined && {
          waitingForApproval: statusMap[qaStatus],
        }),
      };
      const response = await getLanguageGroupedBlocks(
        pageNum,
        true,
        null,
        searchQuery,
        selectedTags.length ? selectedTags : '',
        'write',
        qaStatus !== null,
        true,
        payload,
      );
      if ([2508, 2507].includes(response?.statusCode) && response?.data) {
        setTemplates(prev => (isInitialFetch ? response.data : [...prev, ...response.data]));
        setDataCount(response?.dataCount);
        if (isAllFetched(response)) {
          hasFetchedAllRef.current = true;
        }
        fetchingRef.current = false;
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FETCH_BLOCKS_ERROR.key,
            NEW_TEMPLATE_SECTION.FETCH_BLOCKS_ERROR.value,
          ),
        );
      }
    }
    setLoading(false);
  };

  const clearData = () => {
    setTemplates([]);
    setDataCount(0);
    setPage(1);
    fetchingRef.current = false;
    hasFetchedAllRef.current = false;
  };

  useEffect(() => {
    clearData();
    fetchTemplates(1, true);
  }, [searchQuery, qaStatus, selectedTags, selectedFolder, sortBy, refreshPage]);

  useEffect(() => {
    if (page > 1) {
      fetchTemplates(page, false);
    }
  }, [page]);

  const handleFetchMore = useCallback(() => {
    if (!fetchingRef.current && !hasFetchedAllRef.current) {
      setPage(prev => prev + 1);
      fetchingRef.current = true;
    }
  }, []);

  const getFolderName = folderId => {
    const folderNames = {
      all:
        selectedView === 'documents'
          ? t(NEW_TEMPLATE_SECTION.ALL_DOCUMENTS.key, NEW_TEMPLATE_SECTION.ALL_DOCUMENTS.value)
          : selectedView === 'reusable_pages'
            ? t(
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_PAGES.key,
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_PAGES.value,
            )
            : t(
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_SECTIONS.key,
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_SECTIONS.value,
            ),
    };
    return folderNames[folderId] || folderId.substring(folderId.indexOf('+') + 1);
  };

  const displayText = `${dataCount} ${t(
        NEW_TEMPLATE_SECTION.COMMUNICATIONS.key,
        NEW_TEMPLATE_SECTION.COMMUNICATIONS.value,
      )}`;

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <GridContainer fetchMoreData={handleFetchMore}>
          <Header>
            <HeaderTop>
              <div>
                <HeaderTitle>{getFolderName(selectedFolder)}</HeaderTitle>
                <HeaderSubtitle>{displayText}</HeaderSubtitle>
              </div>

              <Actions>
                <DropdownWrapper>
                  <SortButton open={sortMenuOpen} onClick={() => setSortMenuOpen(!sortMenuOpen)}>
                    <SlidersHorizontalIcon />
                    {t(NEW_TEMPLATE_SECTION.SORT.key, NEW_TEMPLATE_SECTION.SORT.value)}
                    <ChevronDownIconSVG />
                  </SortButton>

                  <DropdownMenu visible={sortMenuOpen}>
                    <MenuItem
                      onClick={() => {
                        setSortBy('date');
                        setSortMenuOpen(false);
                      }}
                    >
                      <span>
                        {t(
                          NEW_TEMPLATE_SECTION.DATE_MODIFIED.key,
                          NEW_TEMPLATE_SECTION.DATE_MODIFIED.value,
                        )}
                      </span>
                      {sortBy === 'date' && <Icon type="check" size={16} />}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setSortBy('name');
                        setSortMenuOpen(false);
                      }}
                    >
                      <span>
                        {t(NEW_TEMPLATE_SECTION.NAME_AZ.key, NEW_TEMPLATE_SECTION.NAME_AZ.value)}
                      </span>
                      {sortBy === 'name' && <Icon type="check" size={16} />}
                    </MenuItem>
                  </DropdownMenu>
                </DropdownWrapper>
              </Actions>
            </HeaderTop>
          </Header>

          {templates.length === 0 ? (
            <EmptyState>
              <EmptyIconWrapper>
                <Icon type="filter" size={40} />
              </EmptyIconWrapper>
              <EmptyTitle>
                {selectedView === 'documents'
                  ? t(
                    NEW_TEMPLATE_SECTION.NO_DOCUMENTS_FOUND.key,
                    NEW_TEMPLATE_SECTION.NO_DOCUMENTS_FOUND.value,
                  )
                  : selectedView === 'reusable_pages'
                    ? t(
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_PAGES_FOUND.key,
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_PAGES_FOUND.value,
                    )
                    : t(
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_SECTIONS_FOUND.key,
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_SECTIONS_FOUND.value,
                    )}
              </EmptyTitle>
              <EmptySubtitle>
                {t(
                  NEW_TEMPLATE_SECTION.TRY_ADJUSTING_FILTERS.key,
                  NEW_TEMPLATE_SECTION.TRY_ADJUSTING_FILTERS.value,
                )}
              </EmptySubtitle>
            </EmptyState>
          ) : (
            <GridLayout>
              {templates.map(template => (
                <DocumentCard
                  key={template.id}
                  selectedView={selectedView}
                  categoryList={categoryList}
                  template={template}
                  highlightLanguage={
                    organizationView === 'languages' ? selectedFolder.split('+')[0] : null
                  }
                  setRefreshPage={setRefreshPage}
                  allLanguages={allLanguages}
                />
              ))}
              {!loading && templates.length > 0 && (
                <div className="observer_div">
                  {templates.length < dataCount ? (
                    <>
                      <Icon type="loading" />{' '}
                      {t(NEW_TEMPLATE_SECTION.LOADING.key, NEW_TEMPLATE_SECTION.LOADING.value)}
                    </>
                  ) : (
                    <p>
                      {t(
                        NEW_TEMPLATE_SECTION.NO_MORE_DATA.key,
                        NEW_TEMPLATE_SECTION.NO_MORE_DATA.value,
                      )}
                    </p>
                  )}
                </div>
              )}
            </GridLayout>
          )}
        </GridContainer>
      )}
    </>
  );
};

export default DocumentGrid;
